import { useApolloClient } from '@apollo/react-hooks'
import {
  Box,
  makeStyles,
  useMediaQuery,
  useTheme,
  useScrollTrigger,
  AnimatePresence,
  motion,
  useIsMobile,
  useUnmount,
  Alert,
  AlertDialog,
  Button,
  ConfirmationDialog,
  Grid,
  Typography
} from '@paintscout/ui'
import type { MuiTheme as Theme, WithUseStyles } from '@paintscout/ui'
import {
  PipelineProgressBar,
  DialogStackPortal,
  useDialogs,
  PageHelpMessage,
  useClientOptions,
  useUser
} from '@paintscout/ui-business'
import ArrowBackIos from '@material-ui/icons/ArrowBackIos'
import type { Contact, Deal, QuoteQueryResult } from '@paintscout/api'
import {
  QuoteDocument as QuoteGQLDocument,
  useContactQuery,
  useDealQuery,
  useSyncActivitiesForDocMutation,
  useUpdateDealStageMutation
} from '@paintscout/api'
import {
  approveAdditionalWork,
  getObjectLabels,
  getPipelines,
  getQuoteAlerts,
  getQuotePresentation,
  getQuoteTemplateOptions,
  isSignatureRequired,
  replaceQuoteItems,
  setContact,
  setSecondaryContact
} from '@paintscout/util/builder'
import { isQuoteEditable } from '@paintscout/util/util'
import * as Sentry from '@sentry/react'
import type { QuoteView, WisetackPromoResponse } from '@paintscout/react-quote'
import {
  AcceptQuoteDialog,
  EditPresentationSectionDialog,
  EditableQuoteContextProvider,
  EditableToggle,
  Presentation,
  Quote,
  useQuote,
  useWisetack
} from '@paintscout/react-quote'
import classnames from 'classnames'
import debounce from 'lodash/debounce'
import find from 'lodash/find'
import findIndex from 'lodash/findIndex'
import isEqual from 'lodash/isEqual'
import { track, info } from 'shared/util/analytics'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import type { EventType, PresentationEstimator, PresentationSection, QuoteDocument } from 'paintscout'
import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { useConnection } from 'react-detect-offline'
import Moment from 'react-moment'
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom'
import { useContactMutations, useQuoteMutations, useQuotePermissions } from 'shared/hooks'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import DocumentTitle from 'src/components/common/DocumentTitle'
import QuoteSidebar from 'src/components/common/QuoteSidebar'
import RoutePrompt from 'src/components/common/RoutePrompt'
import { CopyQuoteDialog, ProcessEventDialog } from 'src/components/dialogs'
import EditQuoteInfoDialog from 'src/components/dialogs/EditQuoteInfoDialog'
import BasePage from 'src/components/layouts/BasePage'
import { useFlags } from 'shared/components/common/FlagsProvider'
import ContactSearchDialogNew from 'src/components/dialogs/ContactSearchDialog/ContactSearchDialogNew'
import QuoteSidebarButton from '../QuoteSidebar/QuoteSidebarButton'
import { useNavContext } from '../Header/SideNav'
import EditableQuoteHeader from './EditableQuoteHeader'
import EditableQuoteTabs from './EditableQuoteTabs'
import RecoveryNotice from './RecoveryNotice'
import { QuoteContactDialog } from './dialogs'
import { getQuoteRecoveryKey } from './quoteRecovery'

export interface EditableQuoteProps extends WithUseStyles<typeof useStyles> {
  isNew?: boolean
  shouldSaveContact?: boolean
  quote: QuoteDocument
  estimator?: PresentationEstimator
  refetching?: boolean

  recoveryData?: {
    quote: QuoteDocument
    time: number
    isNew?: boolean
    shouldSaveContact?: boolean
  }

  disableRecovery?: boolean
  disableDirtyPrompt?: boolean
  onSaved?: (quote: QuoteDocument) => any
  refetch?: () => Promise<any>
}

const useStyles = makeStyles<Theme, { isSticky: boolean; sidebarOpen: boolean; sideNavWidth: number }>(
  (theme: Theme) => ({
    root: {
      position: 'relative',
      width: 'auto',
      paddingTop: 0,
      paddingBottom: 0,
      display: 'flex',
      overflowX: 'hidden',

      '@media print': {
        display: 'block'
      },
      [theme.breakpoints.down('md')]: {
        display: 'block'
      },

      // disables interactivity with the quote
      '&$disabled': {
        pointerEvents: 'none'
      }
    },
    demoMessage: {
      paddingRight: theme.spacing(2)
    },
    progressBarContainer: {
      padding: theme.spacing(1),
      paddingLeft: 0
    },
    backLink: {
      '& button': {
        paddingLeft: 2
      },
      '& svg': {
        fontSize: '1em !important'
      },
      '@media print': {
        display: 'none'
      }
    },
    stickyHeader: ({ sideNavWidth }) => ({
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'stretch',
      background: '#fafafa',
      borderBottom: `1px solid ${theme.palette.divider}`,
      zIndex: theme.zIndex.appBar,
      padding: `${theme.spacing(1)}px 0`,
      paddingTop: `calc(${theme.spacing(1)}px + env(safe-area-inset-top))`,
      paddingLeft: sideNavWidth,
      transition: theme.transitions.create('padding', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      }),
      [theme.breakpoints.down('xs')]: {
        // height: 80
      }
    }),
    stickyHeaderContent: {
      width: '100%',
      maxWidth: 965,
      marginLeft: 'auto',
      marginRight: 'auto',
      [theme.breakpoints.down('md')]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2)
      },
      [theme.breakpoints.up('md')]: {
        maxWidth: (props) => {
          if (props.isSticky && props.sidebarOpen) {
            return 1220
          }

          return 965
        }
      }
    },
    quotePaper: {
      marginTop: theme.spacing(0.5),
      marginBottom: theme.spacing(2),
      marginLeft: 0,
      marginRight: 0
    },
    disabled: {},
    pageContent: {
      position: 'relative',
      justifyContent: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
      width: 718,
      [theme.breakpoints.up('xs')]: {
        width: '90%'
      },
      [theme.breakpoints.up('sm')]: {
        width: theme.breakpoints.values.sm
      },
      [theme.breakpoints.up('md')]: {
        width: 818
      },
      [theme.breakpoints.up('lg')]: {
        width: 974
      },
      [theme.breakpoints.up('xl')]: {
        width: 1230,
        maxWidth: 1440
      },
      display: 'grid',
      gridTemplateColumns: '963px min-content',
      [theme.breakpoints.down('lg')]: {
        gridTemplateColumns: '963px'
      },
      [theme.breakpoints.down('md')]: {
        gridTemplateColumns: '818px'
      },
      [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '100%'
      },

      // move quote to the side when mobile sidebar is open
      '@media screen and (min-width: 768px) and (max-width: 900px)': {
        paddingRight: (props) => (props.sidebarOpen ? '55vw' : null)
      },
      [`@media screen and (min-width: 900px) and (max-width: ${theme.breakpoints.values.xl - 1}px)`]: {
        paddingRight: (props) => (props.sidebarOpen ? '39vw' : null)
      },
      // move quote to the side when desktop sidebar is open
      [theme.breakpoints.up('xl')]: {
        paddingRight: (props) => (props.sidebarOpen ? 280 : null)
      },
      transition: 'padding 0.3s'
    },
    navTabs: {},
    navTab: {
      paddingTop: theme.spacing(),
      paddingBottom: theme.spacing()
    },
    quoteContent: {
      // set max height
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing()
    },
    hiddenPrint: {
      '@media print': {
        display: 'none'
      }
    },
    quote: {}
  }),
  { name: 'EditableQuote' }
)

function EditableQuote({
  quote: cleanQuote,
  estimator,
  isNew,
  recoveryData,
  disableRecovery,
  onSaved,
  refetch,
  ...props
}: EditableQuoteProps) {
  const match = useRouteMatch()
  const theme = useTheme<Theme>()
  const location = useLocation<{ view?: QuoteView }>()
  const { user, trialExpired, isCrew, isViewer, isSuperadmin, isSupport, preferences } = useUser()
  const { options, rates, reset: resetOptions, save: saveOptions } = useClientOptions()
  const { sideNavWidth } = useNavContext()
  const isMobileSidebar = useMediaQuery(theme.breakpoints.down('lg'), { noSsr: true })
  const urlQuery = getUrlQuery<{ view?: QuoteView; message?: string }>({ location })
  const [wisetackPromo, setWisetackPromo] = useState<WisetackPromoResponse>()
  const { saveQuote, acceptAdditionalWork, syncQuote } = useQuoteMutations()
  const [syncActivitiesForDocMutation] = useSyncActivitiesForDocMutation()
  const { openDialog, dismissDialog, dismissAllDialogs } = useDialogs()
  const { crm: crmFlag, canDeactivateSettingsItems } = useFlags()
  const quoteTemplateOptions = getQuoteTemplateOptions({ options, canDeactivate: !!canDeactivateSettingsItems })
  const [updateDealStage] = useUpdateDealStageMutation({
    fetchPolicy: 'no-cache',
    refetchQueries: ['getDeal']
  })
  const { saveContact } = useContactMutations()
  const pipelines = getPipelines({ options })
  const { enqueueSnackbar } = useSnackbar()
  const objectLabels = getObjectLabels({
    options,
    invoice: match.url.includes('invoice')
  })
  const { online } = useConnection()
  const { getPromo } = useWisetack({
    options,
    user,
    preferences
  })
  const isSticky = useScrollTrigger({
    disableHysteresis: true,
    threshold: 100
  })
  const client = useApolloClient()
  const isMobile = useIsMobile()
  const history = useHistory()

  const [state, dispatch] = useReducer(reducer, null, () => {
    let sidebarPage = 'index'
    let sidebarOpen = isMobileSidebar ? false : !!JSON.parse(window.localStorage.getItem('quoteOptions.sidebarOpen'))

    if (urlQuery.message) {
      sidebarPage = 'chat'
      sidebarOpen = true
    }

    return {
      dirtyQuote: isNew ? cleanQuote : null,
      blockDirty: false,
      tab: (urlQuery?.view ?? (isCrew ? 'work-order' : 'quote')) as undefined,
      sidebarPage,
      sidebarOpen,
      presentationPage: '',
      shouldSaveContact: props.shouldSaveContact ?? false
    }
  })

  const { saving, dirtyQuote, blockDirty, tab, sidebarPage, presentationPage, sidebarOpen, shouldSaveContact } = state
  let currentQuote = dirtyQuote || cleanQuote

  // Check our contacts to see if any have been removed, warn user if so.
  const { data: contactData } = useContactQuery({
    skip: !currentQuote?.contact?.id || !currentQuote?.contact?.rev,
    variables: {
      id: currentQuote.contact?.id
    }
  })
  const { data: secondaryContactData } = useContactQuery({
    skip: !currentQuote?.secondaryContact?.id,
    variables: {
      id: currentQuote.secondaryContact?.id
    }
  })
  const {
    data: dealData,
    loading: loadingDeal,
    refetch: refetchDeal
  } = useDealQuery({
    variables: {
      id: currentQuote?.deal_id
    },
    skip: !currentQuote?.deal_id,
    fetchPolicy: 'network-only'
  })

  const {
    permissionToAddAdditionalWork,
    permissionToCreateContact,
    permissionToEdit,
    permissionToEditPresentationSection,
    permissionToEditQuote,
    permissionToSendQuote
  } = useQuotePermissions(currentQuote)

  const canCreateContact = !options.options.quotes.forceIntegrationContacts && permissionToCreateContact

  const [isSavingOptions, setIsSavingOptions] = useState(false)
  const [warnedSecondary, setWarnedSecondary] = useState(false)
  const [warnedPrimary, setWarnedPrimary] = useState(false)
  const [editToggleAnchor, setEditToggleAnchor] = useState<{
    top: number
    left: number
  }>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [deal, setDeal] = useState(dealData?.deal)

  useEffect(() => {
    setDeal(dealData?.deal)
  }, [loadingDeal, dealData])

  useEffect(() => {
    if (location.hash) {
      document.getElementById(location.hash.slice(1)).scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [location.hash])

  const isTemplate = currentQuote.type === 'quoteTemplate'
  const editingTemplate = useMemo(() => {
    return currentQuote.type === 'quoteTemplate' ? find(quoteTemplateOptions, { key: currentQuote._id }) : null
  }, [isSavingOptions])
  const calculationLock = currentQuote?.calculationLock && (!isSuperadmin || !isSupport)
  const classes = useStyles({ isSticky, sidebarOpen: sidebarOpen && !calculationLock, sideNavWidth })
  const presentationTemplate = getQuotePresentation({
    options,
    quote: currentQuote,
    isWorkOrder: false,
    isProductOrderForm: false
  })
  const isEditingSmartTemplate = isTemplate && editingTemplate?.smartTemplate
  const pipeline = pipelines.find((p) => p.key === deal?.pipeline?.value)

  const isTrashed = currentQuote.trashed
  const isDirty = !!dirtyQuote && !blockDirty
  const basicEditablity = (isDirty || isQuoteEditable(cleanQuote) || isEditing) && !trialExpired && !calculationLock
  const isNoteEditable =
    (permissionToEdit || isViewer || isCrew) && basicEditablity && sidebarPage !== 'presentation' && !isTrashed
  const canDeletePayment =
    (permissionToEdit || isViewer) && basicEditablity && sidebarPage !== 'presentation' && !isTrashed
  const canAddAdditionalWork = permissionToAddAdditionalWork && basicEditablity
  const isEditable = permissionToEdit && basicEditablity
  // disable quote interactivity while saving a new quote (due to the URL change, maintaining dirty changes is more work than it's worth)
  const disabled = (isNew && !!saving) || isTrashed

  // Live update smartTemplates when viewing from options
  useEffect(() => {
    if (isEditingSmartTemplate) {
      dispatch({
        type: Action.UPDATE_QUOTE,
        payload: { quote: replaceQuoteItems({ quote: currentQuote, options, rates }), blockDirty: !isNew }
      })
    }
  }, [])

  useEffect(() => {
    const updatePromo = async () => {
      setWisetackPromo(await getPromo(currentQuote))
    }
    const debouncedUpdatePromo = debounce(updatePromo, 2000) // Adjust the delay time as needed
    debouncedUpdatePromo()
  }, [currentQuote?.totals?.balance_due])

  // This works out when to save options after a template type or label change.
  useEffect(() => {
    if (isTemplate) {
      const workingTemplate = quoteTemplateOptions.find((template) => template?.key === editingTemplate?.key)
      const labelChange = workingTemplate?.label !== editingTemplate?.label
      const typeChange = () => {
        if (workingTemplate?.quoteTypes?.length !== editingTemplate?.quoteTypes?.length) {
          return true
        } else {
          return !workingTemplate?.quoteTypes.every((type) => editingTemplate?.quoteTypes?.includes(type))
        }
      }
      if ((labelChange || typeChange()) && !isSavingOptions) {
        handleOptionSave()
      }
    }
  }, [quoteTemplateOptions])

  useEffect(() => {
    if (currentQuote.contact && contactData?.contact?._deleted && !warnedPrimary && !saving) {
      setWarnedPrimary(true)
      enqueueSnackbar(
        `${
          warnedSecondary || (secondaryContactData?.contact?._deleted && currentQuote.secondaryContact)
            ? 'Primary and Secondary Contact have'
            : 'Primary Contact has'
        } been deleted.`,
        { variant: 'warning' }
      )
    }
    if (currentQuote.secondaryContact && secondaryContactData?.contact?._deleted && !warnedSecondary && !saving) {
      setWarnedSecondary(true)
      enqueueSnackbar(
        `${
          warnedPrimary || contactData?.contact?._deleted
            ? 'Primary and Secondary Contact have'
            : 'Secondary Contact has'
        } been deleted.`,
        { variant: 'warning' }
      )
    }
  }, [contactData, secondaryContactData])

  const deleteRecoveryData = useCallback(() => {
    if (cleanQuote?._id) {
      try {
        localStorage.removeItem(getQuoteRecoveryKey(cleanQuote._id))
      } catch (error) {
        console.log('error', error)
        console.log({ localStorage })
        Sentry.captureException(error, {
          level: 'warning' as Sentry.SeverityLevel,
          tags: { quote: cleanQuote._id }
        })
      }
    }
  }, [cleanQuote._id])

  // Store unsaved data in local storage
  const warnedUnrecoverable = useRef<boolean>(false)
  useEffect(() => {
    // don't store initial dirtyQuote when creating new quote or if blockDirty is true
    const isQuoteDirty = isDirty && (!isNew || dirtyQuote !== cleanQuote)
    const quoteShouldBeStored = isQuoteDirty

    // if there's only a dirty contact and no dirty quote, we'll attach it to the clean quote
    const quote = currentQuote

    if (!disableRecovery && quoteShouldBeStored) {
      try {
        localStorage.setItem(
          getQuoteRecoveryKey(quote._id),
          JSON.stringify({
            quote,
            isNew,
            shouldSaveContact,
            time: Date.now()
          })
        )
      } catch (error) {
        console.log('error', error)
        console.log({ localStorage })
        console.log('size', localStorage.size)
        Sentry.captureException(error, {
          level: 'warning' as Sentry.SeverityLevel,
          tags: { quote: quote._id }
        })

        if (!warnedUnrecoverable.current) {
          warnedUnrecoverable.current = true
          enqueueSnackbar(
            'Memory Full - Quote changes will not be recoverable if lost. Try clearing data on your browser or device to free up space.',
            { variant: 'warning' }
          )
        }
      }
    }
  }, [dirtyQuote, location.state, isNew, disableRecovery, cleanQuote, shouldSaveContact, isDirty])

  // temporary to help recover "lost" offline quotes. this is similar to quote recovery
  // except we're not discarding it if the user decides not to recover
  useEffect(() => {
    const quoteLog: Record<string, any> = JSON.parse(localStorage.getItem('quoteLog') || '{}')

    if (dirtyQuote) {
      quoteLog[dirtyQuote._id] = {
        isNew: !!isNew,
        createdOffline: isNew ? !online : null,
        userOffline: !online,
        quote: dirtyQuote
      }
      const keys = Object.keys(quoteLog)
      if (keys.length > 5) {
        const keysToKeep = keys
          .sort((a, b) => (quoteLog[a].quote.dates.updated > quoteLog[b].quote.dates.updated ? -1 : 1))
          .slice(0, 5)

        keys.forEach((key) => {
          if (!keysToKeep.includes(key)) {
            delete quoteLog[key]
          }
        })
      }
    } else if (quoteLog[cleanQuote._id]) {
      quoteLog[cleanQuote._id] = {
        ...quoteLog[cleanQuote._id],
        userOffline: !online,
        saved: true
      }
    }
    try {
      localStorage.setItem('quoteLog', JSON.stringify(quoteLog))
    } catch (error) {
      console.log('error', error)
      console.log({ localStorage, quoteLog })
      console.log('size', localStorage.size)
      Sentry.captureException(error, {
        level: 'warning' as Sentry.SeverityLevel,
        tags: { quote: cleanQuote._id }
      })
    }
  }, [cleanQuote, dirtyQuote, isNew])

  // Clear URL of any sidebar-related params when it closes
  useEffect(() => {
    if (!sidebarOpen && urlQuery.message) {
      setUrlQuery({
        location,
        params: {
          message: null
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sidebarOpen])

  // clear recovery data on unmount
  useUnmount(() => {
    if (!disableRecovery && dirtyQuote) {
      setTimeout(() => {
        // window.ERROR_CAUGHT comes from ErrorBoundary
        if (!window.ERROR_CAUGHT) {
          deleteRecoveryData()
        } else {
          info('Dirty Quote Before Error', JSON.stringify(currentQuote))
        }
      })
    }
  })

  // handle quote recovery data
  useEffect(() => {
    // check localstorage for recovery
    const { quote, time, shouldSaveContact } = recoveryData ?? {}
    const quoteId = cleanQuote && cleanQuote._id

    function handleDiscardRecovery() {
      openDialog(ConfirmationDialog, {
        'data-testid': 'unsaved-quote-dialog-confirm',
        title: 'Are you sure?',
        message: (
          <div>
            {!quote || quote._rev ? (
              <>This will permanently erase the unsaved changes!</>
            ) : (
              <>This will permanently discard the {objectLabels.quote.value} because it was never saved!</>
            )}
          </div>
        ),
        onConfirm: () => {
          dispatch({ type: Action.DISCARD_CHANGES })
          deleteRecoveryData()

          // mark unsaved quote as discarded
          const quoteLog: Record<string, { isNew: boolean; recoveryDiscarded?: boolean; quote: QuoteDocument }> =
            JSON.parse(localStorage.getItem('quoteLog') || '{}')

          if (quoteLog[cleanQuote._id]) {
            quoteLog[cleanQuote._id] = {
              ...quoteLog[cleanQuote._id],
              recoveryDiscarded: true
            }

            try {
              localStorage.setItem('quoteLog', JSON.stringify(quoteLog))
            } catch (error) {
              console.log('error', error)
              console.log({ localStorage, quoteLog })
              console.log('size', localStorage.size)
              Sentry.captureException(error, {
                level: 'warning' as Sentry.SeverityLevel,
                tags: { quote: cleanQuote._id }
              })
            }
          }

          if (isNew) {
            history.goBack()
          } else {
            if (isEditingSmartTemplate) {
              dispatch({ type: Action.UPDATE_QUOTE, payload: {} })
              dispatch({
                type: Action.UPDATE_QUOTE,
                payload: { quote: replaceQuoteItems({ quote: currentQuote, options, rates }), blockDirty: !isNew }
              })
            } else {
              dispatch({ type: Action.UPDATE_QUOTE, payload: {} })
            }
            history.replace(location.pathname, {
              ...(location.state || {}),
              recovery: null
            })
            dismissAllDialogs()
          }
        },
        onCancel: dismissDialog
      })
    }

    if (recoveryData) {
      track('quote_recovered', { quoteId: cleanQuote?._id })
      info('Quote Recovery Found', JSON.stringify(recoveryData))
      dispatch({ type: Action.UPDATE_QUOTE, payload: { quote, shouldSaveContact } })

      // Load unsaved quote if found in local storage
      openDialog(ConfirmationDialog, {
        'data-testid': 'unsaved-quote-dialog',
        title: (
          <div>
            <Typography variant="h3">
              Unsaved {objectLabels.quote.value} {quote.type === 'quoteTemplate' ? ' Template ' : ''}Found
            </Typography>
            <Typography variant="subtitle1" color="textSecondary" style={{ marginTop: 4 }}>
              from {moment(time).format('YYYY-MM-DD [at] h:mma')}
            </Typography>
          </div>
        ),
        message: (
          <div style={{ marginTop: 8 }}>
            <Typography variant="body1" component="p" paragraph={true}>
              This happens when the page unexpectedly closes without a save, or the same{' '}
              {objectLabels.quote.value.toLowerCase()} is opened in multiple tabs.
            </Typography>
            <Typography variant="body1" component="p">
              Would you like to save and continue where you left off?
            </Typography>
          </div>
        ),
        yesLabel: 'Yes, Continue',
        noLabel: `Discard Changes`,
        alignLeftButtonWithTitle: false,
        onConfirm: async () => {
          // Sentry.captureMessage(`Recovery quote recovered from local storage`, {
          //   level: 'info' as Sentry.SeverityLevel
          // })
          const updatedQuote = {
            ...quote,
            dates: {
              ...quote.dates,
              updated: Date.now()
            }
          }

          const handleSaveLogic = () => {
            if (isEditingSmartTemplate) {
              handleSaveQuote({
                quote: replaceQuoteItems({ quote: updatedQuote, options, rates }) as QuoteDocument,
                forceContact: shouldSaveContact
              })
            } else {
              handleSaveQuote({ quote: updatedQuote, forceContact: shouldSaveContact })
            }
          }

          if (!isNew) {
            const { data: latestQuote } = await client
              .query<QuoteQueryResult>({
                fetchPolicy: 'network-only',
                query: QuoteGQLDocument,
                variables: {
                  id: quoteId
                }
              })
              .catch(() => null)

            // quote has newer changes since recovery, ask user if they're sure
            if (latestQuote && latestQuote.quote._rev !== quote._rev) {
              openDialog(ConfirmationDialog, {
                title: `Newer ${objectLabels.quote.value}`,
                message: (
                  <div>
                    Newer changes have been made to this {objectLabels.quote.value} #{quote.number} by someone else.
                    Continuing will <strong>overwrite</strong> them with yours. Are you sure?
                  </div>
                ),
                yesLabel: 'Yes',
                noLabel: `Cancel`,
                onConfirm: () => {
                  handleSaveLogic()
                  dismissAllDialogs()
                },
                onCancel: () => {
                  dismissDialog()
                }
              })
            } else {
              // save changes made to existing quote since there's no newer changes
              handleSaveLogic()
              dismissDialog()
            }
          } else {
            // save new quote
            // The dispach call above doesnt sync shouldSaveContact to state for this call, so we need to force it here if present
            handleSaveLogic()
            dismissDialog()
          }
        },
        onCancel: () => {
          // Sentry.captureMessage(`Recovery Quote discared from local storage`, {
          //   level: 'info' as Sentry.SeverityLevel
          // })
          handleDiscardRecovery()
        }
      })
    }
  }, [])

  // reset any unsaved changes to options when we leave the page
  useEffect(() => {
    return () => resetOptions()
    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    const quote = dirtyQuote
    if (!quote || !quote.presentationTemplate) {
      return
    }

    const updatedPage = find(quote.presentationTemplate.pages, { key: presentationPage })
    if (!(updatedPage?.active ?? true)) {
      dispatch({ type: Action.SET_PRESENTATION_PAGE, payload: { presentationPage: null } })
    }
    // eslint-disable-next-line
  }, [dirtyQuote])

  // Toggle sidebar open when "edit-payment-settings" event is received

  useEffect(() => {
    if (typeof BroadcastChannel != 'undefined') {
      const channel = new BroadcastChannel('quote-sidebar')

      const handleMessage = (event: MessageEvent) => {
        if (event.data === 'edit-payment-settings') {
          dispatch({ type: Action.SET_SIDEBAR_PAGE, payload: { sidebarPage: 'payments' } })
        }
      }

      channel.addEventListener('message', handleMessage)

      return () => {
        channel.removeEventListener('message', handleMessage)

        channel.close()
      }
    }
  }, [])

  const handleOptionSave = async () => {
    setIsSavingOptions(true)
    await saveOptions()
    setIsSavingOptions(false)
  }

  const handleSyncQuote = useCallback(async () => {
    // should save if dirty, want to only call this w/ clean quote,
    // else should just be saving and resolving conflicts that way
    if (isDirty) {
      return null
    }
    track('quote_synced', { quoteId: currentQuote._id })
    const { data = {} } = await syncQuote({ id: currentQuote._id, rev: currentQuote._rev })
    if (data?.syncQuote) {
      dispatch({ type: Action.UPDATE_QUOTE, payload: { quote: data.syncQuote, blockDirty: true } })
      return data.syncQuote
    } else {
      return null
    }
  }, [syncQuote])

  const handleSaveQuote = useCallback(
    async (args: {
      quote: QuoteDocument
      /**
       * Forces a save if it would be rejected
       */
      force?: boolean
      /**
       * Forces a contact save if it would be out of sync in state
       */
      forceContact?: boolean
    }) => {
      let quote: QuoteDocument = {
        ...args.quote,
        dates: {
          ...args.quote.dates,
          // incase it's been a long time between last change and actual save, we'll update `updated`
          // so it's more accurate for conflict resolution
          updated: Date.now()
        }
      }

      try {
        dispatch({ type: Action.SAVE_START, payload: { quote } })
        let needContactSync = false
        if (quote.contact && (shouldSaveContact || args?.forceContact)) {
          const { id, rev, ...contact } = quote.contact

          const savedContact = await saveContact({
            contact: { ...contact, _id: id, _rev: rev } as Contact
          }).then((res) => res.contact)

          quote = setContact({ quote, contact: savedContact, currentDealId: quote?.source?.dealId })
          if (quote?.secondaryContact && quote.secondaryContact?.id) {
            const { id: secondaryId, rev: secondaryRev, ...secondaryContact } = quote.secondaryContact
            const savedSecondaryContact = await saveContact({
              contact: { ...secondaryContact, _id: secondaryId, _rev: secondaryRev } as Contact
            }).then((res) => res.contact)
            quote = setSecondaryContact({ quote, secondaryContact: savedSecondaryContact })
          }

          if (
            contactData?.contact?._id === savedContact?._id &&
            (contactData?.contact?.first_name !== savedContact?.first_name ||
              contactData?.contact?.last_name !== savedContact?.last_name)
          ) {
            needContactSync = true
          }

          // because the contact was saved & updated in the quote, we're going to re-dispatch Action.SAVE_START
          // so that the state.saving.quote is correct
          dispatch({ type: Action.SAVE_START, payload: { quote } })
        }

        const { quote: savedQuote } = await saveQuote({
          quote,
          force: args.force
        })

        if (!savedQuote) {
          dispatch({ type: Action.SAVE_FINISH })
          return null
        }

        // If we have updated the name we need to sync our activities for this quote, will trigger this to happen async
        if (needContactSync) {
          await syncActivitiesForDocMutation({ variables: { id: quote._id } })
        }

        deleteRecoveryData()
        dispatch({ type: Action.SAVE_FINISH })
        track('quote_saved', { quoteId: savedQuote._id })
        currentQuote = savedQuote

        if (!isQuoteEditable(savedQuote) && isEditing) {
          setIsEditing(false)
        }

        if (onSaved) {
          onSaved(savedQuote)
        }

        return savedQuote
      } catch (e) {
        info('Dirty Quote Before Error', JSON.stringify(args.quote))
        if (e.message?.includes(`Save Rejected:`)) {
          // start refetching right away
          refetch()

          const balanceDue = 'balance due'
          const additionalWork = 'additional work'
          openDialog(ConfirmationDialog, {
            title: `Cannot Save ${objectLabels.quote.value}`,
            scary: true,
            message:
              e.message?.includes(balanceDue) || e.message?.includes(additionalWork) ? (
                <div>
                  This {objectLabels.quote.value.toLowerCase()}&lsquo;s{' '}
                  {e.message?.includes(balanceDue) ? balanceDue : additionalWork} has been changed while you were
                  editing. We recommend discarding your changes and reviewing the updated version.
                  <br />
                  <br />
                  You may decide to save anyway, but you <strong>might overwrite an important update.</strong>
                </div>
              ) : (
                <div>
                  This {objectLabels.quote.value.toLowerCase()} has been accepted or declined while you were editing. We
                  recommend discarding your changes and reviewing the updated version.
                  <br />
                  <br />
                  You may decide to save anyway, but you will{' '}
                  <strong>
                    overwrite the accepted or declined version of the {objectLabels.quote.value.toLowerCase()}
                  </strong>{' '}
                  and it will need to be accepted again.
                </div>
              ),

            yesLabel: 'Save Anyway',
            noLabel: 'Discard',
            onConfirm: () => {
              handleSaveQuote({ quote, force: true })
              dismissDialog()
            },
            onCancel: () => {
              dispatch({ type: Action.DISCARD_CHANGES })
              deleteRecoveryData()
              dismissDialog()
            }
          })
        } else {
          console.log(dirtyQuote)
          console.log(e.error ?? e)
          Sentry.captureException(e, { tags: { quoteId: dirtyQuote?._id } })
          const message =
            e?.graphQLErrors?.[0]?.message || `An error occurred while saving the ${objectLabels.quote.value}`
          enqueueSnackbar(message, { variant: 'error' })
        }
        dispatch({ type: Action.SAVE_ERROR, payload: { error: e, dirtyQuote: quote } })
      }
    },
    [
      shouldSaveContact,
      saveQuote,
      isTemplate,
      deleteRecoveryData,
      isEditing,
      onSaved,
      saveContact,
      saveOptions,
      refetch,
      openDialog,
      objectLabels.quote.value,
      dismissDialog,
      enqueueSnackbar
    ]
  )

  const handleEditContact = useCallback(
    ({ isSecondary = false, isNew = false }: { isSecondary?: boolean; isNew?: boolean }) => {
      return openDialog(QuoteContactDialog, {
        isSecondary,
        isNew,
        onConfirm: (quote: QuoteDocument, shouldSaveContact: boolean) => {
          dispatch({ type: Action.UPDATE_QUOTE, payload: { quote, shouldSaveContact } })
          dismissDialog()
        },
        onCancel: dismissDialog
      })
    },
    [dismissDialog, openDialog]
  )

  const handleSelectContact = useCallback(
    async (isSecondary: boolean) => {
      track('quote_contact_set', { quoteId: currentQuote._id, isSecondary })
      return openDialog(ContactSearchDialogNew, {
        onCreateNew: canCreateContact
          ? (queryString: string) => {
              openDialog(
                QuoteContactDialog,
                {
                  isNew: true,
                  isSecondary,
                  queryString,
                  onConfirm: (quote: QuoteDocument, shouldSaveContact: boolean) => {
                    dispatch({ type: Action.UPDATE_QUOTE, payload: { quote, shouldSaveContact } })
                    dismissDialog()
                  },
                  onCancel: dismissDialog
                },
                { replace: true }
              )
            }
          : null,
        onConfirm: (newContact: Contact, currentDealId?: number | string, syncProvider?: string) => {
          const quote = isSecondary
            ? setSecondaryContact({ quote: currentQuote, secondaryContact: newContact })
            : setContact({ quote: currentQuote, contact: newContact, currentDealId })
          if (syncProvider) {
            openDialog(ProcessEventDialog, {
              provider: syncProvider,
              title: 'Syncing Contact Details',
              async: false,
              eventTypeOverride: 'quote-sync' as EventType,
              params: {
                quote
              },
              onConfirm: (result: any) => {
                const syncedQuote = result.result
                console.log({ syncedQuote })
                dispatch({
                  type: Action.UPDATE_QUOTE,
                  payload: {
                    quote: syncedQuote,
                    shouldSaveContact: true
                  }
                })
                dismissAllDialogs()
              },
              onClose: () => {
                dismissDialog()
              }
            })
          } else {
            dispatch({
              type: Action.UPDATE_QUOTE,
              payload: {
                quote,
                shouldSaveContact: true
              }
            })
            dismissDialog()
          }
        },
        onCancel: dismissDialog
      })
    },
    [openDialog, dismissDialog, dirtyQuote, cleanQuote]
  )

  const handleRemoveSecondaryContact = useCallback(async () => {
    track('quote_secondary_contact_removed', { quoteId: currentQuote._id })
    return openDialog(ConfirmationDialog, {
      title: `Confirm Removal`,
      message: <div>Are you sure you want to remove the secondary contact?</div>,
      yesLabel: 'Yes',
      noLabel: `Cancel`,
      onConfirm: () => {
        dispatch({
          type: Action.UPDATE_QUOTE,
          payload: {
            quote: setSecondaryContact({ quote: currentQuote }),
            shouldSaveContact: true
          }
        })
        dismissDialog()
      },
      onCancel: () => {
        dismissDialog()
      }
    })
  }, [openDialog, dirtyQuote, cleanQuote, dismissDialog])

  function handleToggleSidebar() {
    dispatch({
      type: Action.SIDEBAR_TOGGLE
    })
  }

  // Accept additional work depending on network status
  async function handleAcceptByNetwork(quote: QuoteDocument, signature?: string, note?: string) {
    let updatedQuote: QuoteDocument = quote
    if (online && quote?._rev) {
      // Only use full accept if online and quote has a _rev
      const res = await acceptAdditionalWork({
        rev: quote._rev,
        signature,
        quoteId: quote._id,
        note
      })

      const { quote: mutatedQuote } = res.data.acceptAdditionalWork
      updatedQuote = mutatedQuote
      deleteRecoveryData()
    } else {
      // Offline accept, no notification but works offline/ w/o save
      const approvedQuote = approveAdditionalWork({
        quote,
        options,
        note,
        view: quote.defaultView === 'substrate' ? 'substrate' : 'area',
        signature
      })
      updatedQuote = await handleSaveQuote({ quote: approvedQuote })
    }

    dispatch({
      type: Action.UPDATE_QUOTE,
      payload: { quote: updatedQuote, blockDirty: true }
    })
    dismissDialog()
  }

  async function handleAccept() {
    const quote = currentQuote
    const requireSignature = isSignatureRequired({ options, quote, path: 'requireSignatureAdditionalWork' })
    const allowAdditionalApproval = quote.is_invoice || quote.status.value === 'accepted'
    if (!allowAdditionalApproval) {
      enqueueSnackbar(`The ${objectLabels.quote.value} must be accepted before approving additional work`, {
        variant: 'error'
      })
    } else {
      const updatedQuote = isDirty ? await handleSaveQuote({ quote }) : quote
      if (requireSignature) {
        return openDialog(AcceptQuoteDialog, {
          quote: updatedQuote,
          options,
          allowNote: true,
          additionalAccept: true,
          onConfirm: async (event: React.MouseEvent<HTMLElement>, signature: string, note?: string) => {
            await handleAcceptByNetwork(updatedQuote, signature, note)
          },
          onCancel: () => {
            dismissDialog()
          }
        })
      } else {
        await handleAcceptByNetwork(updatedQuote)
      }
    }
  }

  const handlePresentationClick = (key: string) => {
    const quote = currentQuote

    const pageIndex = presentationPage ? findIndex(presentationTemplate.pages, { key: presentationPage }) : 0
    if (pageIndex < 0) {
      return false
    }

    const sectionIndex = findIndex(presentationTemplate.pages[pageIndex].sections, { key })
    const section = presentationTemplate.pages[pageIndex].sections[sectionIndex]
    if (!section?.advanced?.allowCustom) {
      return false
    }

    openDialog(EditPresentationSectionDialog, {
      quote,
      section: presentationTemplate.pages[pageIndex].sections[sectionIndex],
      onConfirm: (section: PresentationSection) => {
        const updatedPages = [...presentationTemplate.pages]
        updatedPages[pageIndex].sections[sectionIndex] = section

        dispatch({
          type: Action.UPDATE_QUOTE,
          payload: { quote: { ...quote, presentationTemplate: { ...presentationTemplate, pages: updatedPages } } }
        })

        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  async function handleStageClick(updatedDeal: Deal) {
    await updateDealStage({
      variables: {
        dealId: updatedDeal._id,
        stage: updatedDeal.stage.value
      }
    })
    setDeal(updatedDeal)
    // setRefreshToggle((prev) => !prev)
  }

  const handleEditInfo = () => {
    openDialog(EditQuoteInfoDialog, {
      onConfirm: async () => {
        dismissDialog()
        if (refetchDeal) {
          await refetchDeal()
        }
      },
      onCancel: dismissDialog
    })
  }

  // Check for mis-saves on imported quotes, resaves if lacking _rev after save
  const [hasSaved, setHasSaved] = useState(false)
  const needsSave = cleanQuote._rev === null && 'PK' in cleanQuote
  useEffect(() => {
    if (needsSave && !hasSaved && online) {
      setHasSaved(true)
      handleSaveQuote({ quote: cleanQuote })
    }
  }, [needsSave])

  return (
    <EditableQuoteContextProvider
      quote={currentQuote}
      presentationPage={presentationPage}
      sidebarPage={sidebarPage}
      estimator={estimator}
      isDirty={isDirty}
      isEditable={isEditable && sidebarPage !== 'presentation' && !isTrashed}
      isNoteEditable={isNoteEditable}
      canDeletePayment={canDeletePayment}
      hasActiveAccount={!trialExpired}
      canAddAdditionalWork={canAddAdditionalWork}
      isSaving={!!saving || isSavingOptions}
      quoteTab={tab}
      wisetackPromo={currentQuote?.contact?.company ? null : wisetackPromo}
      onSave={handleSaveQuote}
      onSync={handleSyncQuote}
      onReset={() => dispatch({ type: Action.DISCARD_CHANGES })}
      onEditContact={() => handleEditContact({})}
      onSelectContact={() => handleSelectContact(false)}
      onCreateContact={() => handleEditContact({ isNew: true })}
      onEditSecondaryContact={() => handleEditContact({ isSecondary: true })}
      onCreateSecondaryContact={() => handleEditContact({ isSecondary: true, isNew: true })}
      onSelectSecondaryContact={() => handleSelectContact(true)}
      onRemoveSecondaryContact={handleRemoveSecondaryContact}
      editingTemplate={editingTemplate}
      onEditDetails={() => dispatch({ type: Action.SET_SIDEBAR_PAGE, payload: { sidebarPage: 'details' } })}
      onEditNote={() => dispatch({ type: Action.SET_SIDEBAR_PAGE, payload: { sidebarPage: 'advanced' } })}
      onAccept={handleAccept}
      onChange={(updatedQuote, blockDirty) =>
        dispatch({ type: Action.UPDATE_QUOTE, payload: { quote: updatedQuote, blockDirty } })
      }
      onSidebarPageChange={(sidebarPage) => dispatch({ type: Action.SET_SIDEBAR_PAGE, payload: { sidebarPage } })}
      onPresentationPageChange={(presentationPage) =>
        dispatch({ type: Action.SET_PRESENTATION_PAGE, payload: { presentationPage } })
      }
      onEditInfo={handleEditInfo}
    >
      <>
        <RoutePrompt
          when={isDirty}
          message={(newLocation) => {
            const state = newLocation?.state as any
            if (state?.skipDirtyPrompt) {
              return true
            } else {
              track('Dirty Quote Prompt')
              info('Dirty Quote Before Prompt', JSON.stringify(dirtyQuote))
              return 'You have unsaved changes, are you sure you want to leave this page?'
            }
          }}
        />
        <DialogStackPortal />
        <DocumentTitle title={`Edit ${objectLabels.quote.value}`} />
        <AnimatePresence>
          {isSticky && (
            <motion.div
              key="sticky-header"
              className={classes.stickyHeader}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div className={classes.stickyHeaderContent}>
                <EditableQuoteHeader
                  pipeline={pipeline}
                  classes={{ root: classnames(classes.root, { [classes.disabled]: disabled }) }}
                  sticky
                  isNew={isNew}
                  view={tab}
                  handleChangeView={(newTab) => {
                    track('quote_view_changed', { quoteId: currentQuote._id, fromView: tab, toView: newTab })
                    dispatch({ type: Action.SET_TAB, payload: { tab: newTab, isMobileSidebar, location } })
                  }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <BasePage classes={{ root: classnames(classes.root, { [classes.disabled]: disabled }) }}>
          <div className={classes.pageContent}>
            <motion.div className={classnames(classes.quoteContent)} layout="position" transition={{ duration: 0.2 }}>
              {isTrashed && <RecoveryNotice />}
              <DemoMessage classes={classes} currentQuote={currentQuote} />
              {!isMobile && (
                <div className={classes.backLink}>
                  <Button
                    onClick={() => window.history.back()}
                    variant="text"
                    size="small"
                    startIcon={<ArrowBackIos classes={{ root: classes.backLink }} />}
                  >
                    Back
                  </Button>
                </div>
              )}

              <EditableQuoteHeader
                pipeline={pipeline}
                isNew={isNew}
                view={tab}
                handleChangeView={(newTab) => {
                  track('quote_view_changed', { quoteId: currentQuote._id, fromView: tab, toView: newTab })
                  dispatch({ type: Action.SET_TAB, payload: { tab: newTab, isMobileSidebar, location } })
                }}
              />

              <QuoteAlerts />
              {deal && crmFlag && !isCrew && (
                <div className={classes.progressBarContainer}>
                  <PipelineProgressBar quoteView handleStageClick={handleStageClick} deal={deal} pipeline={pipeline} />
                </div>
              )}
              <EditableQuoteTabs
                selectedTabValue={tab}
                onTabChange={(newTab) => {
                  dispatch({ type: Action.SET_TAB, payload: { tab: newTab, isMobileSidebar, location } })
                }}
              />

              <div className={classes.quoteWrapper}>
                <EditableToggle
                  position={editToggleAnchor}
                  editable={isEditable}
                  disabled={trialExpired || (!permissionToEdit && !permissionToAddAdditionalWork)}
                  onToggle={() => {
                    setIsEditing(true)
                  }}
                  onClose={() => setEditToggleAnchor(null)}
                >
                  {tab === 'presentation' ? (
                    <Presentation
                      page={presentationPage}
                      classes={classes}
                      onPresentationClick={handlePresentationClick}
                    />
                  ) : (
                    <Quote
                      view={tab as QuoteView}
                      online={online}
                      classes={{ paper: classes.quotePaper }}
                      onClickCapture={(ev: any) => {
                        // only call ev.stopPropagation when we're not editable
                        const allowPropagation =
                          ev?.target?.id === 'allow-propagation' || ev?.target?.offsetParent?.id === 'allow-propagation'

                        if (calculationLock) {
                          ev.stopPropagation()
                          openDialog(AlertDialog, {
                            title: 'Calculations Locked',
                            message: `This ${objectLabels.quote.value.toLowerCase()} may not be compatible with the latest version of PaintScout. To edit it, copy to a new estimate and make changes there.`,
                            onClose: dismissDialog,
                            actions: [
                              {
                                label: 'Cancel',
                                onClick: dismissDialog,
                                left: true,
                                variant: 'text'
                              },
                              {
                                label: `Copy to New ${objectLabels.quote.value}`,
                                onClick: () => {
                                  openDialog(CopyQuoteDialog, {
                                    quoteId: currentQuote._id,
                                    calculate: true,
                                    isInvoice: currentQuote.is_invoice
                                  })
                                }
                              }
                            ]
                          })
                          return
                        }

                        if (!isEditable && !canAddAdditionalWork && !allowPropagation) {
                          ev.stopPropagation()
                          setEditToggleAnchor({
                            top: ev.clientY,
                            left: ev.clientX
                          })
                        }
                      }}
                    />
                  )}
                </EditableToggle>
              </div>
            </motion.div>
            <QuoteSidebar
              classes={{ sidebar: classes.sidebar }}
              open={sidebarOpen && !calculationLock}
              currentPage={sidebarPage}
              editable={isEditable}
              permissionToEdit={permissionToEdit && !trialExpired}
              permissionToEditQuote={permissionToEditQuote && !trialExpired}
              permissionToEditPresentationSection={permissionToEditPresentationSection && !trialExpired}
              permissionToSendQuote={permissionToSendQuote}
              onClose={handleToggleSidebar}
              onToggleEdit={(editStatus: boolean) => {
                setIsEditing(editStatus)
              }}
            />
            <QuoteSidebarButton show={(isMobile || !sidebarOpen) && !calculationLock} onClick={handleToggleSidebar} />
          </div>
        </BasePage>
      </>
    </EditableQuoteContextProvider>
  )
}

function DemoMessage({ classes, currentQuote }) {
  const quote = currentQuote

  if (quote.demoMessage) {
    return (
      <div className={classes.demoMessage}>
        <PageHelpMessage
          path={`existing-quote-${quote._id}`}
          title={quote.demoTitle}
          openContent={quote.demoMessage}
          closedContent={null}
        />
      </div>
    )
  } else {
    return null
  }
}

export type QuoteAlertItem = {
  key?: string
  severity: 'warning' | 'error' | 'info' | 'success'
  title?: string | null
  tooltip?: string | null
  centerIcon?: boolean
  content: React.ReactNode
}

interface QuoteAlertsProps {
  additionalAlerts?: QuoteAlertItem[]
}

export function QuoteAlerts({ additionalAlerts = [] }: QuoteAlertsProps) {
  const [dirtyTime, setDirtyTime] = useState(0) // time that the quote was made dirty
  const { quote, isDirty, cleanQuote } = useQuote()
  const { options } = useClientOptions()

  const objectLabels = getObjectLabels({ options, invoice: quote.is_invoice })

  useEffect(() => {
    setDirtyTime(0)
    const nextDirtyTime = Date.now()
    if (isDirty) {
      const timeout = setTimeout(
        () => {
          setDirtyTime(nextDirtyTime)
        },
        1000 * 60 * 10
      ) // 10 minutes

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [isDirty])

  const quoteAlerts = [
    ...additionalAlerts,
    ...getQuoteAlerts({ quote: quote || cleanQuote, options }),
    // add warning that quote hasn't been saved
    dirtyTime && {
      severity: 'warning' as const,
      title: null,
      tooltip: null,
      content: (
        <>
          {objectLabels.quote.value} was edited{' '}
          <Moment date={dirtyTime} fromNow={true} ago element={({ children }) => <span>{children}</span>} />
          {''} ago, and changes have not been saved.
        </>
      )
    },
    quote.sources?.serviceTitan?.dealId && {
      key: 'serviceTitan',
      severity: 'info' as const,
      title: null,
      tooltip: null,
      centerIcon: true,
      content: (
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>This {objectLabels.quote.value} has been created in ServiceTitan</Grid>
          <Grid item>
            <Button
              variant="underline"
              size="medium"
              href={`https://go.servicetitan.com/#/Job/Index/${quote.sources?.serviceTitan?.dealId}`}
            >
              View In ServiceTitan
            </Button>
          </Grid>
        </Grid>
      )
    }
  ].filter(Boolean) as QuoteAlertItem[]

  if (!quoteAlerts.length) {
    return null
  }

  return (
    <Box
      marginTop={1}
      marginRight={2}
      sx={{
        // @ts-ignore - not sure why there's a TS error here, it's valid
        '@media print': {
          display: 'none'
        }
      }}
    >
      <Grid container spacing={1}>
        {quoteAlerts.map((alert, i) => (
          <Grid key={alert.key ?? i} item xs={12}>
            <Alert
              severity={alert.severity}
              variant={'default'}
              content={alert.content}
              title={alert.title}
              tooltip={alert.tooltip}
              centerIcon={alert.centerIcon}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}

interface EditableQuoteState {
  /**
   * contains the quote, contact & secondaryContact that is being saved, or null if no save is happening
   */
  saving?: {
    quote: QuoteDocument
    contact?: Contact
    secondaryContact?: Contact
  }
  dirtyQuote?: QuoteDocument
  blockDirty?: boolean
  shouldSaveContact?: boolean
  tab?: QuoteView
  sidebarOpen?: boolean
  sidebarPage?: string
  presentationPage?: string
}

enum Action {
  SAVE_START,
  SAVE_ERROR,
  SAVE_FINISH,
  UPDATE_QUOTE,
  SET_TAB,
  SET_SIDEBAR_PAGE,
  SET_PRESENTATION_PAGE,
  SIDEBAR_TOGGLE,
  DISCARD_CHANGES
}

function reducer(state: EditableQuoteState, action: { type: Action; payload?: any }): EditableQuoteState {
  switch (action.type) {
    case Action.SAVE_START:
      return {
        ...state,
        dirtyQuote: action.payload.quote,
        saving: {
          quote: action.payload.quote,
          contact: action.payload.contact
        }
      }
    case Action.SAVE_FINISH: {
      const quoteChanged = !isEqual(state.saving?.quote, state.dirtyQuote)
      return {
        ...state,
        blockDirty: false,
        dirtyQuote: quoteChanged ? state.dirtyQuote : null,
        shouldSaveContact: false,
        saving: null
      }
    }
    case Action.SAVE_ERROR:
      return {
        ...state,
        dirtyQuote: action.payload.dirtyQuote ?? state.dirtyQuote,
        saving: null,
        shouldSaveContact: true
      }
    case Action.UPDATE_QUOTE:
      return {
        ...state,
        blockDirty: action.payload?.blockDirty ?? false,
        dirtyQuote: action.payload.quote,
        shouldSaveContact: action.payload.shouldSaveContact ?? state.shouldSaveContact
      }
    case Action.SET_TAB: {
      const { tab, isMobileSidebar, location } = action.payload
      let newSidebarPage = state.sidebarPage
      let newPresentationPage = state.presentationPage

      setUrlQuery({
        location,
        params: { view: tab },
        replaceParams: true
      })

      if (tab === 'presentation') {
        newSidebarPage = 'presentation'
      } else if (tab !== 'presentation' && state.sidebarPage === 'presentation') {
        newSidebarPage = null
        newPresentationPage = null
      }

      return {
        ...state,
        tab,
        sidebarPage: newSidebarPage,
        sidebarOpen: !isMobileSidebar && tab === 'presentation' ? true : state.sidebarOpen,
        presentationPage: newPresentationPage
      }
    }
    case Action.SIDEBAR_TOGGLE:
      window.localStorage.setItem('quoteOptions.sidebarOpen', JSON.stringify(!state.sidebarOpen))
      return { ...state, sidebarOpen: !state.sidebarOpen }
    case Action.SET_SIDEBAR_PAGE: {
      const { sidebarPage } = action.payload
      if (state.sidebarPage === sidebarPage && state.sidebarOpen) {
        return state
      }
      return {
        ...state,
        sidebarPage,
        tab: sidebarPage === 'presentation' ? 'presentation' : state.tab,
        sidebarOpen: true
      }
    }
    case Action.SET_PRESENTATION_PAGE: {
      const { presentationPage } = action.payload
      return { ...state, presentationPage }
    }
    case Action.DISCARD_CHANGES:
      return {
        ...state,
        shouldSaveContact: false,
        dirtyQuote: null
      }
  }
}

export default EditableQuote
